/**
 * La lista de colaboradores (administración, 4/11/2026): añadir y quitar.
 *
 *  · «Añadir colaborador»: se elige un usuario con perfil de médico que aún
 *    no esté en la lista (de `GET /api/colaboradores/medicos-posibles`) y,
 *    si se quiere, su % fijo y desde cuándo vale (por defecto, hoy). Si el
 *    médico no es usuario de la app, no sale: primero hay que darle de alta
 *    en Usuarios (rol médico); se dice aquí. El % fijo se manda SIEMPRE
 *    (vacío = sin % fijo, el general): al volver a añadir a alguien, lo que
 *    se ve en la casilla (que llega rellena con el que tenía) es lo que queda.
 *  · «Quitar»: con confirmación. Es una baja blanda: su % fijo y sus
 *    excepciones por servicio dejan de aplicarse a los actos nuevos (vuelven
 *    si se le añade otra vez); sus actos, lo que se le debe y sus
 *    liquidaciones se conservan, y con actos sigue saliendo como «dado de baja».
 * Aquí no hay ni un nombre ni un porcentaje: todo viene del servidor, que lo
 * vuelve a comprobar (403, 409 si ya está, 422) y lo apunta en Control rastreo.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { dateToStr } from '../../lib/citasApi';
import { leerImporte } from '../../lib/importeEuros';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

export const CLASE_SELECT = 'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm '
  + 'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50';

/** El % escrito («47,5»): número de 0 a 100, null si vacío, NaN si no vale. */
export function leerPct(texto) {
  if (!String(texto ?? '').trim()) return null;
  const n = leerImporte(texto);
  if (n === null || Number.isNaN(n) || n < 0 || n > 100) return Number.NaN;
  return n;
}

/** Hoy en la hora de este equipo, «aaaa-mm-dd». */
export const hoyLocal = () => dateToStr(new Date());

/** «47,5» para la casilla (sin el símbolo). */
export const pctParaCampo = (v) => (v === null || v === undefined ? ''
  : String(Math.round(Number(v) * 100) / 100).replace('.', ','));

/**
 * Props: { medicos: [{id, name, role}], enLista: [medico_id], inicial (id que
 * viene elegido, p. ej. desde la tarjeta de un médico que no está en la
 * lista), anteriores ({medico_id: % fijo que tenía, de los dados de baja}),
 * onClose, onAnadido }
 */
export function AnadirColaboradorDialog({ medicos, enLista, inicial = '', anteriores = {}, onClose, onAnadido }) {
  const ya = new Set(enLista || []);
  const elegibles = (medicos || []).filter((m) => (!m.role || m.role === 'doctor') && !ya.has(m.id));
  const primero = elegibles.some((m) => m.id === inicial) ? inicial : '';
  const [medicoId, setMedicoId] = useState(primero);
  const [pct, setPct] = useState(pctParaCampo(anteriores[primero]));
  const [desde, setDesde] = useState(hoyLocal());
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    const n = leerPct(pct);
    if (Number.isNaN(n)) { setError('El % fijo: escribe un número de 0 a 100 (por ejemplo 45 o 47,5), o déjalo vacío.'); return; }
    setError(''); setGuardando(true);
    try {
      // Siempre el % (null = sin % fijo): lo que se ve es lo que queda.
      await api.post('/api/colaboradores/medicos', { medico_id: medicoId, pct_fijo: n, desde: desde || null });
      toast.success('Colaborador añadido a la lista');
      onAnadido();
    } catch (e) { setError(mensajeError(e, 'No se pudo añadir')); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="colab-anadir-dialogo">
        <DialogHeader>
          <DialogTitle>Añadir colaborador</DialogTitle>
          <DialogDescription>
            Elige el médico. Solo salen los usuarios con perfil de médico que aún no están en la lista: si el
            colaborador no aparece, dale de alta primero en Usuarios con el rol de médico.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="colab-anadir-medico">Médico</Label>
            <select id="colab-anadir-medico" className={CLASE_SELECT} value={medicoId}
              onChange={(e) => {
                setError(''); setMedicoId(e.target.value); setPct(pctParaCampo(anteriores[e.target.value]));
              }} data-testid="colab-anadir-medico">
              <option value="">Elige un médico</option>
              {elegibles.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            {elegibles.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="colab-anadir-vacio">
                No queda ningún médico por añadir. Para uno nuevo, dalo de alta primero en Usuarios (rol médico).
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="colab-anadir-pct">% fijo (opcional)</Label>
              <Input id="colab-anadir-pct" inputMode="decimal" value={pct} placeholder="Vacío: % general"
                onChange={(e) => { setError(''); setPct(e.target.value); }} className="text-right tabular-nums"
                data-testid="colab-anadir-pct" />
            </div>
            <div>
              <Label htmlFor="colab-anadir-desde">Desde</Label>
              <Input id="colab-anadir-desde" type="date" value={desde}
                onChange={(e) => { setError(''); setDesde(e.target.value); }} data-testid="colab-anadir-desde" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="colab-anadir-pct-ayuda">
            {pct.trim()
              ? 'Ese % fijo se aplica a sus actos desde esa fecha, salvo los que tengan excepción.'
              : 'Sin % fijo: a sus actos se les aplica el % general de la clínica (salvo excepciones).'}
          </p>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="colab-anadir-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={guardando}>Cancelar</Button>
            <Button type="button" onClick={guardar} disabled={!medicoId || guardando}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-anadir-guardar">
              {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Añadir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Props: { medico: {medico_id, medico_nombre, pendiente}, onClose, onQuitado } */
export function QuitarColaboradorDialog({ medico, onClose, onQuitado }) {
  const [quitando, setQuitando] = useState(false);
  const [error, setError] = useState('');
  const quitar = async () => {
    setQuitando(true); setError('');
    try {
      await api.delete(`/api/colaboradores/medicos/${encodeURIComponent(medico.medico_id)}`);
      toast.success('Colaborador quitado de la lista');
      onQuitado();
    } catch (e) { setError(mensajeError(e, 'No se pudo quitar')); }
    finally { setQuitando(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" role="alertdialog" data-testid="colab-quitar-dialogo">
        <DialogHeader>
          <DialogTitle>¿Quitar a {medico.medico_nombre || 'este médico'} de la lista?</DialogTitle>
          <DialogDescription>
            Deja de salir en la lista de colaboradores, y su % fijo y sus excepciones por servicio dejan de
            aplicarse a los actos nuevos (vuelven si se le añade otra vez). No se borra nada: sus actos, lo que se
            le debe y sus liquidaciones se conservan, y mientras tenga actos seguirá saliendo como «dado de baja».
            Se puede volver a añadir cuando se quiera.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-700" role="alert" data-testid="colab-quitar-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={quitando}>Cancelar</Button>
          <Button type="button" variant="destructive" onClick={quitar} disabled={quitando}
            data-testid="colab-quitar-confirmar">
            {quitando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Quitar de la lista
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
